import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAccount, useDisconnect, useBalance, useSwitchChain } from 'wagmi';
import { useWeb3Modal } from '@web3modal/wagmi/react';
import { base } from 'wagmi/chains';
import { useAuth } from './AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WalletContext = createContext(null);

export function WalletProvider({ children }) {
  const { user, updateUser, isAuthenticated, token } = useAuth();
  const [error, setError] = useState(null);

  // Wagmi hooks
  const { address, isConnected, chain, connector } = useAccount();
  const { disconnect: wagmiDisconnect } = useDisconnect();
  const { switchChain } = useSwitchChain();
  const { data: balanceData } = useBalance({ address, chainId: base.id });

  // Web3Modal hook
  const { open } = useWeb3Modal();

  const walletAddress = address || user?.wallet_address || null;
  const chainId = chain?.id || null;
  const ethBalance = balanceData ? parseFloat(balanceData.formatted).toFixed(4) : '0';
  const isOnBase = chainId === base.id;

  // Sync wallet to backend when connected
  useEffect(() => {
    if (address && isAuthenticated && token) {
      syncWalletToBackend(address);
    }
  }, [address, isAuthenticated, token]);

  const syncWalletToBackend = async (addr) => {
    try {
      await axios.post(`${API}/wallet/connect`, { wallet_address: addr }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      updateUser({ wallet_address: addr });
    } catch (err) {
      console.error('Failed to sync wallet:', err);
    }
  };

  const connectWallet = useCallback(async () => {
    setError(null);
    try {
      await open();
    } catch (err) {
      setError(err.message || 'Failed to open wallet modal');
    }
  }, [open]);

  const disconnectWallet = useCallback(async () => {
    wagmiDisconnect();
    if (isAuthenticated && token) {
      try {
        await axios.post(`${API}/wallet/disconnect`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        updateUser({ wallet_address: null });
      } catch (err) {
        console.error('Failed to disconnect wallet:', err);
      }
    }
  }, [wagmiDisconnect, isAuthenticated, updateUser, token]);

  const switchToBase = useCallback(async () => {
    try {
      switchChain({ chainId: base.id });
    } catch (err) {
      setError('Failed to switch to Base Chain');
    }
  }, [switchChain]);

  const formatAddress = (addr) => {
    if (!addr) return '';
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  const value = {
    walletAddress,
    address: walletAddress,
    chainId,
    ethBalance,
    kutroBalance: user?.kutro_balance || 0,
    isConnecting: false,
    isConnected: isConnected || !!walletAddress,
    isOnBase,
    isMetaMaskAvailable: typeof window !== 'undefined' && !!window.ethereum?.isMetaMask,
    error,
    activeConnector: connector,
    connectWallet,
    disconnectWallet,
    switchToBase,
    formatAddress,
    openModal: open,
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
}

export function useWallet() {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
}
